import React, { useState, useEffect } from 'react';
import { X, Send, FolderPlus } from 'lucide-react';
import { collection, addDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import './ClientProjectForm.css';

const ClientProjectForm = ({ onSave, onCancel, isBudgetRequest = false, currentUser, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    budget: '',
    deadline: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        budget: initialData.budget || '',
        deadline: initialData.deadline
          ? new Date(initialData.deadline.seconds * 1000).toISOString().split('T')[0]
          : ''
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter a project name');
      return;
    }

    if (isBudgetRequest) {
      setSubmitting(true);
      try {
        if (initialData?.id) {
          await updateDoc(doc(db, 'budgetRequests', initialData.id), {
            name: formData.name,
            description: formData.description,
            budget: formData.budget ? parseFloat(formData.budget) : null,
            deadline: formData.deadline ? new Date(formData.deadline) : null,
          });
          onSave({ isBudgetRequest: true, name: formData.name, isEdit: true });
        } else {
          await addDoc(collection(db, 'budgetRequests'), {
            name: formData.name,
            description: formData.description,
            budget: formData.budget ? parseFloat(formData.budget) : null,
            deadline: formData.deadline ? new Date(formData.deadline) : null,
            clientId: currentUser.uid,
            clientName: currentUser.displayName || currentUser.email,
            status: 'pending',
            createdAt: Timestamp.now()
          });
          onSave({ isBudgetRequest: true, name: formData.name });
        }
      } catch (error) {
        alert('Error submitting request: ' + error.message);
      } finally {
        setSubmitting(false);
      }
    } else {
      onSave({
        name: formData.name,
        description: formData.description,
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        deadline: formData.deadline ? new Date(formData.deadline) : null
      });
    }
  };

  const isEditing = isBudgetRequest && !!initialData;

  return (
    <div className="modal-overlay">
      <div className="modal-content client-project-form">
        <div className="modal-header">
          <h2>
            {isBudgetRequest
              ? isEditing ? 'Edit Budget Request' : 'Request a Budget'
              : 'Create New Project'}
          </h2>
          <button onClick={onCancel} className="btn-close">
            <X size={20} />
          </button>
        </div>

        {isBudgetRequest && !isEditing && (
          <div className="budget-request-notice">
            <p>Describe your project and we'll get back to you with a quote.</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">
              {isBudgetRequest ? 'Project / Service Name' : 'Project Name'} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={isBudgetRequest ? 'e.g. Logo Design, Website, Brand Identity...' : 'e.g. Brand Redesign, Website Development'}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              {isBudgetRequest ? 'What do you need?' : 'Description'}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={isBudgetRequest
                ? 'Describe in detail what you need, references, style preferences...'
                : 'Describe what you need for this project...'}
              rows={4}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="budget">
                {isBudgetRequest ? 'Estimated Budget (€)' : 'Budget (€)'}
              </label>
              <input
                type="number"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deadline">
                {isBudgetRequest ? 'Desired Deadline' : 'Deadline'}
              </label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {isBudgetRequest
                ? <><Send size={16} />{submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Send Request'}</>
                : <><FolderPlus size={16} />Create Project</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientProjectForm;